import { useEffect, useRef, FC, MutableRefObject } from 'react';
import WaveSurfer from 'wavesurfer.js';
import CursorPlugin from 'wavesurfer.js/src/plugin/cursor';
import RegionsPlugin from 'wavesurfer.js/src/plugin/regions'; // https://wavesurfer-js.org/plugins/regions.html
import TimelinePlugin from 'wavesurfer.js/src/plugin/timeline'; // https://wavesurfer-js.org/example/timeline/index.html
import SpectrogramPlugin from 'wavesurfer.js/src/plugin/spectrogram'; // https://wavesurfer-js.org/api/file/src/plugin/spectrogram/index.js.html
import { waveSurferObjFamily } from 'packages/main/components/audio-tool/atoms';
import { useSetRecoilState } from 'recoil';
import { AudioAnnotationTypes } from 'packages/main/components/audio-tool/audio-tool-types';
import { SPECTROGRAM_HEIGHT } from 'packages/main/components/audio-tool/audio-tool-main';
import { devLog } from 'packages/main/components/audio-tool/utils';

interface WavesurferWrapperProps {
  annotationType: AudioAnnotationTypes;
  timelineContainerRef: MutableRefObject<HTMLDivElement | null>;
  spectrogramContainerRef?: MutableRefObject<HTMLDivElement | null>;
  normalize: boolean;
  regionMinLength: number;
  startingHeight: number;
  width?: number;
}

export const WaveSurferWrapper: FC<
  React.PropsWithChildren<WavesurferWrapperProps>
> = ({
  annotationType,
  timelineContainerRef,
  spectrogramContainerRef,
  regionMinLength,
  normalize,
  startingHeight,
  width,
  children,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const setWaveSurferObj = useSetRecoilState(
    waveSurferObjFamily(annotationType)
  );

  const allowAnnotations =
    annotationType !== AudioAnnotationTypes.NoAnnotations;

  // Init: create the WaveSurfer (along with any relevant plugins) in the DOM
  useEffect(() => {
    let waveSurfer: WaveSurfer | null = null;
    if (containerRef.current) {
      devLog('Creating waveSurfer: ', annotationType);

      const plugins = [
        CursorPlugin.create({
          showTime: true,
          opacity: '1',
          customShowTimeStyle: {
            'background-color': '#000',
            color: '#fff',
            padding: '2px',
            'font-size': '10px',
          },
        }),
      ];

      if (allowAnnotations) {
        plugins.push(
          RegionsPlugin.create({
            regionsMinLength: regionMinLength,
            regions: [],
            dragSelection: false,
          })
        );
      }

      if (spectrogramContainerRef && spectrogramContainerRef.current) {
        plugins.push(
          SpectrogramPlugin.create({
            container: spectrogramContainerRef.current,
            labels: true,
            height: SPECTROGRAM_HEIGHT,
            //  colorMap: spectogramColorMap,
            //    windowFunc: 'triangular',
          })
        );
      }

      // Note: most wavesurfers will have the timeline hidden by the parent setting the div's display css to 'none'
      if (timelineContainerRef.current) {
        plugins.push(
          TimelinePlugin.create({
            container: timelineContainerRef.current,
          })
        );
      }

      waveSurfer = WaveSurfer.create({
        container: containerRef.current,
        responsive: true,
        normalize: normalize,
        height: startingHeight,
        plugins: plugins,
      });

      if (allowAnnotations) {
        // Enable drag to create new regions, but don't allow dragging to move existing regions
        waveSurfer.enableDragSelection({ drag: false });
      }

      // This triggers init functions in audio-tool-wavesurfer-row
      setWaveSurferObj(waveSurfer);
    }

    return () => {
      if (waveSurfer) {
        devLog('Destroying waveSurfer:', annotationType);
        setWaveSurferObj(null); // Bug fix: must clean up this Recoil AtomFamily so tool works properly on next load
        waveSurfer.destroy();
        waveSurfer = null;
      }
    };
    // This is an init effect that runs once after component mount, so we don't want any deps OTHER than width.
    // We destroy and re-created when width changes, because that's the only way to get the Spectrogram plugin to resize itself to the new width.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  return <div ref={containerRef} />;
};
